import React from 'react';
import {Popup} from 'react-leaflet';
import Button from "@mui/material/Button";
import {
    Chip,
    Paper,
    Typography
} from "@mui/material";
import Constants from "../../../utils/constants.js";
import "./ConteneurMarkerPopup.css"
import {helper} from "../../../utils/helper.js";
import {isExploitant} from "../../../utils/roles.js";
import {useNavigate} from "react-router-dom";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import {synthesesActions} from "../../../modules/syntheses/syntheses.actions.js";
import {useDispatch} from "react-redux";
import TypologieLavageBadge from "../layout/TypologieLavageBadge.js";
import Grid from "@mui/material/Grid2";
import {
    getChecklistColor,
    getChecklistEtatLabel,
    getChecklistElementsEnDefaut,
    CHECKLIST_ETAT,
} from "../../../modules/conteneurMap/conteneurMap.helpers.js";

/**
 * Bloc « État maintenance » de la micro-card (PAT-04 R10) : état + date + éléments en défaut
 * (ou raison de la tentative impossible) + nb photos + lien vers l'intervention (INT-02).
 */
const ChecklistBlock = ({conteneur}) => {
    const c = conteneur?.checklist;
    const etat = c?.etat ?? CHECKLIST_ETAT.NON_INSPECTE;
    const enDefaut = getChecklistElementsEnDefaut(c);
    const date = c?.date ? `${helper.formatDate(c.date)} ${helper.getFormattedTime(c.date)}` : null;
    return (
        <Grid container size={12} sx={{padding: '.5rem', borderBottom: '1px solid #e6e6e6'}}>
            <Grid size={12}>
                <Typography className={"marker-popup-label"}>État maintenance :</Typography>
                <Chip size="small" label={getChecklistEtatLabel(etat)}
                      sx={{backgroundColor: getChecklistColor(c), color: 'white', ml: 1, fontWeight: 600}}/>
                {etat === CHECKLIST_ETAT.PROBLEME && c?.nbProblemes > 1 && (
                    <Typography component="span" className={"marker-popup-label"} sx={{ml: 1}}>
                        {c.nbProblemes} éléments
                    </Typography>
                )}
            </Grid>
            {date && (
                <Grid size={12}>
                    <Typography className={"marker-popup-label"}>
                        {etat === CHECKLIST_ETAT.TENTATIVE_IMPOSSIBLE ? 'Tentative du' : 'Inspecté le'} : {date}
                    </Typography>
                </Grid>
            )}
            {enDefaut.length > 0 && (
                <Grid size={12} sx={{display: 'flex', flexWrap: 'wrap', gap: .5, alignItems: 'center'}}>
                    <Typography className={"marker-popup-label"} component="span">En défaut :</Typography>
                    {enDefaut.map((e, i) => (
                        <Chip key={e.code ?? i} size="small" variant="outlined" label={e.libelle ?? e.code}
                              sx={{borderColor: getChecklistColor(c), color: '#213067'}}/>
                    ))}
                </Grid>
            )}
            {etat === CHECKLIST_ETAT.TENTATIVE_IMPOSSIBLE && (
                <Grid size={12}>
                    <Typography className={"marker-popup-label"}>Raison : {c?.raison}</Typography>
                </Grid>
            )}
            {(c?.photos?.length ?? 0) > 0 && (
                <Grid size={12} className="popup-checklist-gallery">
                    {c.photos.map((ph, i) => (
                        <a key={`${ph.path}-${i}`}
                           href={`${Constants.API_ASSETS_PHOTO_INTERVENTION}/${ph.path}`}
                           target="_blank" rel="noreferrer"
                           className="popup-checklist-thumb" title={ph.element ?? 'Photo'}>
                            <img src={`${Constants.API_ASSETS_PHOTO_INTERVENTION}/${ph.path}`} alt={ph.element ?? 'Photo'}/>
                            {ph.element && <span>{ph.element}</span>}
                        </a>
                    ))}
                </Grid>
            )}
        </Grid>
    );
};

const ConteneurMarkerPopup = ({conteneur, onClose, onEdit, checklistMode = false}) => {
    const getPreviewPhoto = () => {
        const li = conteneur?.lastIntervention;
        if (!li) return conteneur?.dernierLavagePhotoApres ?? null; // carte v2 (GeoJSON) : photo « après » seule
        const order = ['photosApres', 'photosAvant', 'photosInaccessible', 'photosProblem'];
        for (const key of order) {
            const arr = li?.[key];
            if (arr && arr.length > 0) return arr[0];
        }
        return null;
    };
    const previewPhoto = getPreviewPhoto();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [lavageImpossible, setLavageImpossible] = React.useState(!conteneur.lastIntervention?.isAccessible)
    const [hasMaintenance, setHasMaintenance] = React.useState((conteneur.lastIntervention?.isAccessible && conteneur.lastIntervention?.problemes.length > 0))

    const onPDFClick = (interventionId, interventionDate) => {
        dispatch(synthesesActions.fetchInterventionPDF(interventionId, interventionDate))
    }

    return (
        <Popup closeButton={true} className="marker-popup">
            <Paper elevation={0} className={"marker-popup-container"}>
                <Grid container size={12}>
                    <Grid size={12}>
                        <Typography variant={"subtitle1"} className={"marker-popup-title"}>
                            {conteneur.nomenclature} : {conteneur.adresse}, {conteneur.commune}
                        </Typography>
                    </Grid>
                    <Grid container size={12}>
                        {checklistMode && <ChecklistBlock conteneur={conteneur}/>}
                        {previewPhoto && !checklistMode && (
                            <Grid size={12} sx={{ textAlign: 'center', padding: '.5rem' }}>
                                <img
                                    src={`${Constants.API_ASSETS_PHOTO_INTERVENTION}/${previewPhoto}`}
                                    alt="Photo conteneur"
                                    className="popup-marker-photo"
                                />
                            </Grid>
                        )}
                        <Grid container size={12} sx={{padding: '.5rem'}}>
                            <Grid size={12}>
                                <Typography className={"marker-popup-label"}>Type de déchet : </Typography>
                                <Chip size={'small'} label={conteneur.dechetType}
                                      sx={helper.getColorForDechetType(conteneur.dechetType)}/>
                            </Grid>
                            <Grid size={12}>
                                <Typography className={"marker-popup-label"}>Typologie : </Typography>
                                <Chip size={'small'} label={conteneur.conteneurType}/>
                            </Grid>
                            <Grid size={12}>
                                <Typography className={"marker-popup-label"}>
                                    Nombre
                                    de {helper.getConteneurTypeLibelle(conteneur.conteneurType)} : {conteneur.quantite}
                                </Typography>
                            </Grid>

                            {(
                                <>
                                    <Grid size={12}>
                                        {helper.formatDate(conteneur.lastInterventionDate) !== '' &&
                                            <Typography className={"marker-popup-label"}>
                                                Dernier lavage
                                                : {helper.formatDate(conteneur.lastInterventionDate)} {helper.getFormattedTime(conteneur.lastInterventionDate)}
                                            </Typography>
                                        }
                                    </Grid>
                                    <Grid size={12}>
                                        <Typography className={"marker-popup-label"}>
                                            Type de prestation : {conteneur.lastIntervention?.prestation}
                                        </Typography>
                                    </Grid>
                                    <Grid size={12}>
                                        <Typography className={"marker-popup-label"}>
                                            Type de lavage :
                                        </Typography>
                                        <TypologieLavageBadge typologie={conteneur.lastIntervention?.typologieLavage} sx={{ml: 2}}/>
                                    </Grid>
                                    <Grid size={12}>
                                        <Typography className={"marker-popup-label"}>
                                            Statut :
                                        </Typography>
                                        <Chip
                                            label={helper.getInterventionStatus(conteneur.lastIntervention)}
                                            sx={{...helper.getInterventionStatusColor(conteneur.lastIntervention), ml: 1}}
                                            size="small"
                                        />
                                    </Grid>
                                    {hasMaintenance &&
                                        <Grid size={12}>

                                        </Grid>
                                    }
                                    {lavageImpossible &&
                                        <Grid size={12}>
                                            <Typography className={"marker-popup-label"}>
                                                Raison :
                                            </Typography>
                                            {conteneur.lastIntervention?.problemes && conteneur.lastIntervention?.problemes.map((p, index) => (
                                                <Typography key={index} className={"marker-popup-label"} component={"span"}>
                                                    {p.libelle}
                                                </Typography>
                                            ))}

                                        </Grid>
                                    }
                                </>
                            )}

                        </Grid>
                    </Grid>
                        <Grid container size={12} className={"popup-marker-actions-container"}
                              justifyContent={"space-evenly"}>
                            {isExploitant() &&
                                <Grid>
                                    <Button
                                        color="primary"
                                        size="small"
                                        variant="contained"
                                        onClick={() => onEdit(conteneur)}>
                                        Déplacer la borne
                                    </Button>
                                </Grid>
                            }
                            <Grid>
                                <Button
                                    color="primary"
                                    size="small"
                                    variant="contained"
                                    onClick={() => {
                                        const base = Constants.APP_ROUTE_INTERVENTIONS.replace(":id", conteneur.id);
                                        const target = checklistMode && conteneur?.checklist?.interventionId;
                                        navigate(target ? `${base}?intervention=${target}` : base);
                                    }}>
                                    Détail
                                </Button>
                            </Grid>

                            <Grid>
                                <Button
                                    color="primary"
                                    size="small"
                                    variant="contained"
                                    onClick={() => onPDFClick(conteneur.lastIntervention?.id, conteneur.lastIntervention?.dateEvenement)}
                                    startIcon={<PictureAsPdfIcon/>}
                                    sx={{mt: {xs: 2, sm: 0}}}
                                >
                                    Télécharger la fiche PDF
                                </Button>
                            </Grid>
                        </Grid>
                </Grid>

            </Paper>
        </Popup>
    );
};

export default ConteneurMarkerPopup;
